// Typed wrappers for the subtitles subsystem
// (`/api/v1/subtitles/*`, `/api/v1/settings/subtitles`,
// `/api/v1/subtitle-providers/*`).
//
// Keeps the same shape the backend serialises in
// `crates/subtitles/src/{model,settings,provider,routes}.rs`. Renaming
// a field there means renaming it here too — that's checked at
// typecheck time once `web/src/pages/settings/Subtitles.tsx` consumes
// the type.

import { api } from "@/lib/api";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type MediaKind = "movie" | "episode";

export type SubtitleSource =
  | "embedded"
  | "external"
  | "downloaded"
  | "extracted"
  | "ocr"
  | "translated"
  | "manual";

export interface SubtitleRow {
  id: number;
  media_kind: string;
  media_id: number;
  language: string;
  sdh: boolean;
  forced: boolean;
  source: string;
  provider: string | null;
  provider_id: string | null;
  score: number | null;
  path: string;
  file_hash: string | null;
  bytes: number | null;
  created_at: string;
  updated_at: string;
}

/** Per-language entry in a profile. Order in the parent array is
 *  preference order — first language is tried first. */
export type HiPreference = "required" | "excluded" | "prefer";

export interface SubtitleLanguageEntry {
  language: string;
  required: boolean;
  hi: HiPreference;
}

export interface SubtitleLanguagesProfile {
  languages: SubtitleLanguageEntry[];
}

export interface SubtitleSettings {
  movie: SubtitleLanguagesProfile;
  tv: SubtitleLanguagesProfile;
  cutoff_score: number;
  upgrade_allowed: boolean;
  /** #84: fire the provider chain on every successful import. */
  auto_search_on_import: boolean;
  /** Remux imports to drop embedded tracks outside the language profile. */
  strip_embedded_on_import: boolean;
  /** Keep embedded tracks with no language tag when stripping. */
  keep_untagged_embedded: boolean;
}

/** Sparse PUT — fields the user didn't touch are omitted. */
export interface UpdateSubtitleSettings {
  movie?: SubtitleLanguagesProfile;
  tv?: SubtitleLanguagesProfile;
  cutoff_score?: number;
  upgrade_allowed?: boolean;
  auto_search_on_import?: boolean;
  strip_embedded_on_import?: boolean;
  keep_untagged_embedded?: boolean;
}

/** One file the embedded-track sweep changed (or would change). */
export interface SweepExample {
  path: string;
  /** Language tags of the removed tracks, in stream order. */
  removed: string[];
  kept: number;
}

/** Progress of the embedded-track library clean-up. The last run's
 *  numbers stay readable until the next one starts. */
export interface SweepStatus {
  running: boolean;
  dry_run: boolean;
  total: number;
  processed: number;
  changed: number;
  tracks_removed: number;
  /** Files left alone because no track matched the keep list. */
  no_kept_track: number;
  failed: number;
  bytes_saved: number;
  current: string | null;
  last_error: string | null;
  cancelled: boolean;
  started_at: string | null;
  finished_at: string | null;
  examples: SweepExample[];
  /** False when ffmpeg / ffprobe aren't installed on the server. */
  tools_available: boolean;
}

export interface SubtitleProviderRow {
  id: string;
  kind: string;
  enabled: boolean;
  priority: number;
  config_json: string;
  created_at: string;
  updated_at: string;
}

/** Sparse PATCH for a provider row. `secret` follows the three-state
 *  convention: undefined = leave existing, "" = clear, non-empty =
 *  replace. */
export interface ProviderPatchBody {
  enabled?: boolean;
  priority?: number;
  config_json?: string;
  secret?: string;
}

/** Body shape for `POST /api/v1/subtitle-providers`. `id` defaults to
 *  `kind` server-side; we only send it when the user wants to run two
 *  instances of the same kind side-by-side. */
export interface CreateProviderBody {
  id?: string;
  kind: string;
  enabled?: boolean;
  priority?: number;
  config_json?: string;
}

/** Result body from `POST /api/v1/subtitle-providers/:id/test`. */
export interface ProviderTestResult {
  ok: boolean;
  error?: string;
}

/** Per-provider settings shape persisted as JSON in `config_json`.
 *  Today only `opensubtitles.com` exists; future kinds add their own
 *  fields here. The frontend round-trips the full object so unknown
 *  keys round-trip safely without us learning them. */
export interface OpenSubtitlesConfig {
  api_key: string;
  username?: string | null;
  password?: string | null;
  base_url?: string | null;
  user_agent?: string | null;
}

// -----------------------------------------------------------------------------
// Wanted page (#81)
// -----------------------------------------------------------------------------

/** One row on the Wanted (Subtitles) page — a single (media, language) gap. */
export interface WantedSubtitle {
  media_kind: MediaKind;
  media_id: number;
  /** Movie title or "Series · S01E02 — Episode title". */
  title: string;
  poster_url: string | null;
  /** ISO-639-1 code. */
  language: string;
  /** ISO timestamp of when the gap was first observed. */
  required_since: string;
  last_attempt_at: string | null;
  /** "found" | "not-found" | "error" once an attempt has been made. */
  last_attempt_status: string | null;
}

/** Body of POST /api/v1/subtitles/search — single-row search action. */
export interface SearchOneBody {
  media_kind: MediaKind;
  media_id: number;
  languages: string[];
}

export interface LanguageOutcome {
  language: string;
  /** "found" | "not-found" | "error". */
  status: string;
  error?: string | null;
  provider?: string | null;
  score?: number | null;
}

export interface SearchOneResponse {
  found: number;
  languages: LanguageOutcome[];
}

export interface BulkTarget {
  media_kind: MediaKind;
  media_id: number;
  /** Empty defaults to the user's required language list server-side. */
  languages: string[];
}

export interface BulkSearchBody {
  /** Omit to bulk-search the full Wanted list (capped by `limit`). */
  targets?: BulkTarget[];
  limit?: number;
}

export interface BulkOutcome extends LanguageOutcome {
  media_kind: MediaKind;
  media_id: number;
}

export interface BulkSearchResponse {
  attempted: number;
  found: number;
  results: BulkOutcome[];
}

// -----------------------------------------------------------------------------
// Manual picker (#87)
// -----------------------------------------------------------------------------

/** Per-component contribution to a candidate's score. Components sum
 *  (clamped to 0..=100) to the candidate's `score`. */
export interface ScoreBreakdown {
  popularity: number;
  hash_match: number;
  release_match: number;
  framerate_match: number;
  hi_match: number;
}

/** One row in the manual picker. `download_url` is opaque — the
 *  frontend round-trips it to `subtitles/download` verbatim. */
export interface CandidateRow {
  provider: string;
  language: string;
  release_title?: string | null;
  uploader?: string | null;
  download_count?: number | null;
  rating?: number | null;
  score: number;
  score_breakdown: ScoreBreakdown;
  sdh: boolean;
  forced: boolean;
  framerate?: number | null;
  upload_date?: string | null;
  download_url: string;
}

export interface ListCandidatesBody {
  media_kind: MediaKind;
  media_id: number;
  language: string;
}

export interface ListCandidatesResponse {
  candidates: CandidateRow[];
  /** Per-provider error strings collected while gathering. Empty on
   *  the happy path; non-empty when at least one provider failed. */
  errors: string[];
}

export interface DownloadCandidateBody {
  media_kind: MediaKind;
  media_id: number;
  language: string;
  provider: string;
  /** Opaque ref from a CandidateRow.download_url. */
  download_url: string;
}

export interface DownloadCandidateResponse {
  subtitle_id: number;
  provider: string;
}

// -----------------------------------------------------------------------------
// API
// -----------------------------------------------------------------------------

export const subtitlesApi = {
  // ---- Settings -----------------------------------------------------------
  settings: {
    get: () => api<SubtitleSettings>("/api/v1/settings/subtitles"),
    update: (body: UpdateSubtitleSettings) =>
      api<SubtitleSettings>("/api/v1/settings/subtitles", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
  },

  // ---- Embedded-track clean-up --------------------------------------------
  embeddedSweep: {
    status: () => api<SweepStatus>("/api/v1/subtitles/embedded/sweep"),
    start: (dryRun: boolean) =>
      api<SweepStatus>("/api/v1/subtitles/embedded/sweep", {
        method: "POST",
        body: JSON.stringify({ dry_run: dryRun }),
      }),
    cancel: () =>
      api<SweepStatus>("/api/v1/subtitles/embedded/sweep", {
        method: "DELETE",
      }),
  },

  // ---- Providers ----------------------------------------------------------
  providers: {
    list: () => api<SubtitleProviderRow[]>("/api/v1/subtitle-providers"),
    create: (body: CreateProviderBody) =>
      api<SubtitleProviderRow>("/api/v1/subtitle-providers", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    patch: (id: string, body: ProviderPatchBody) =>
      api<SubtitleProviderRow>(
        `/api/v1/subtitle-providers/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(body),
        },
      ),
    remove: (id: string) =>
      api<void>(`/api/v1/subtitle-providers/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    test: (id: string) =>
      api<ProviderTestResult>(
        `/api/v1/subtitle-providers/${encodeURIComponent(id)}/test`,
        { method: "POST" },
      ),
  },

  // ---- Per-media subtitles ------------------------------------------------
  forMedia: (kind: MediaKind, mediaId: number) =>
    api<SubtitleRow[]>(
      `/api/v1/subtitles/${kind}/${mediaId}`,
    ),
  remove: (id: number) =>
    api<void>(`/api/v1/subtitles/${id}`, { method: "DELETE" }),

  // ---- Wanted page (#81) --------------------------------------------------
  wanted: (limit: number = 1000) =>
    api<WantedSubtitle[]>(`/api/v1/subtitles/wanted?limit=${limit}`),
  search: (body: SearchOneBody) =>
    api<SearchOneResponse>("/api/v1/subtitles/search", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  bulkSearch: (body: BulkSearchBody) =>
    api<BulkSearchResponse>("/api/v1/subtitles/wanted/search-all", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ---- Manual picker (#87) ------------------------------------------------
  candidates: (body: ListCandidatesBody) =>
    api<ListCandidatesResponse>("/api/v1/subtitles/candidates", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  download: (body: DownloadCandidateBody) =>
    api<DownloadCandidateResponse>("/api/v1/subtitles/download", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
